import {
  CROP_LOADING,
  SET_CROP_ENDPOINT_CALLED,
  SET_CROP_DATA,
  SET_MD5_DATA,
  SET_INITIAL_REGION_DATA,
  SET_IS_ANY_REGION_DELETED,
  SET_ANNOTATION_INDEX,
  CROP_ERROR,
  SET_SELECTED_CROP_REGION,
  CROP_DRAWING,
  SET_CROP_AREA_SIZE,
  SET_CROPPED_IMAGE_URL,
  SET_VERTICAL_CROP_SELECTED,
  SHOW_NEXT_BUTTON_ENABLED_TOOLTIP,
  SET_ROTATION_AMOUNT,
  SET_ROTATION_MODE,
} from '../constants/actionTypes';

/**
 * Action that is dispatched when the loading crop key should be updated.
 * @param  {Boolean} status
 * @return {Object}
 */
export function setLoading(status) {
  return {
    type: CROP_LOADING,
    value: status,
  };
}

/**
 * Action that is dispatched when the crop area data needs to be updated.
 * @param  {Array} cropData The array of crop boxes.
 * @return {Object}
 */
export function setCropData(cropData) {
  return {
    type: SET_CROP_DATA,
    value: cropData,
  };
}

/**
 * Action that is dispatched after response from autocrop BE API.
 * @param  {String} md5 MD5 of font.
 * @return {Object}
 */
export function setMD5(md5) {
  return {
    type: SET_MD5_DATA,
    value: md5,
  };
}

/**
 * Action that is dispatched after response from autocrop BE API.
 * @param  {Array} initialRegionData Array of initial crop data from BE API.
 * @return {Object}
 */
export function setInitialRegionData(initialRegionData) {
  return {
    type: SET_INITIAL_REGION_DATA,
    value: initialRegionData,
  };
}

/**
 * Action that is dispatched after ondelete event is triggered.
 * @param  {Boolean} isAnyRegionDeleted region deletion bool value
 * @return {Object}
 */
export function setIsAnyRegionDeleted(isAnyRegionDeleted) {
  return {
    type: SET_IS_ANY_REGION_DELETED,
    value: isAnyRegionDeleted,
  };
}

/**
 * Action that is dispatched after response from autocrop BE API.
 * @param  {Number} annotationIndex Annotation index of croped region.
 * @return {Object}
 */
export function setAnnotationIndex(annotationIndex) {
  return {
    type: SET_ANNOTATION_INDEX,
    value: annotationIndex,
  };
}

/**
 * Action that is dispatched when a crop area is selected.
 * @param  {Object} selectionData
 * @property {Number} selectedCropRegion The selected crop area index
 * @property {Array} cropData The new cropData after the selection
 * @return {Object}
 */
export function setSelectedCropRegion(selectionData) {
  return {
    type: SET_SELECTED_CROP_REGION,
    value: selectionData,
  };
}

/**
 * Action that is dispatched when the error code needs to be updated.
 * @param  {String} errorCode The error code that the component will match and show.
 * @return {Object}
 * @property {String} type      Type of the action
 * @property {String} errorCode Code of the error
 */
export function setError(errorCode) {
  return {
    type: CROP_ERROR,
    errorCode,
  };
}

/**
 * Action that sets an object containing the height and width of the cropping
 * area.
 * @param {Object} size
 * @property {Number} width
 * @property {Number} height
 * @return {Object}
 * @property {String} type  `SET_CROP_AREA_SIZE` constant
 * @property {Object} value The `size` parameter
 */
export function setCropAreaSize(size) {
  return {
    type: SET_CROP_AREA_SIZE,
    value: size,
  };
}

/**
 * Action that sets the flag telling if the crop area is in draw mode or not
 * (for new crop box creation).
 * @param {Boolean} drawingMode The drawing flag.
 * @return {Object}
 * @property {String} type  `CROP_DRAWING` constant
 * @property {Boolean} value The `drawingMode` parameter
 */
export function setDrawingMode(drawingMode) {
  return {
    type: CROP_DRAWING,
    value: drawingMode,
  };
}

/*
 * Action that is dispatched when the cropped image Url data needs to be updated.
 * @param  {String} croppedImageUrl The cropped image object url.
 * @return {Object}
 */
export function setCroppedImageUrl(croppedImageUrl) {
  return {
    type: SET_CROPPED_IMAGE_URL,
    croppedImageUrl,
  };
}

/**
 * Action that is dispatched when a vertical crop is selected/deselected.
 * @param  {Boolean} status
 * @return {Object}
 */
export function setVerticalCropSelected(status) {
  return {
    type: SET_VERTICAL_CROP_SELECTED,
    value: status,
  };
}

/**
 * Action that sets the amount of degrees the cropped image is rotated.
 * @param {Boolean} rotationAmount Degrees of rotation.
 * @return {Object}
 * @property {String} type  `SET_ROTATION_AMOUNT` constant
 * @property {Boolean} value The `rotationAmount` parameter
 */
export function setRotationAmount(rotationAmount) {
  return {
    type: SET_ROTATION_AMOUNT,
    value: rotationAmount,
  };
}

/**
 * Action that is dispatched when the showNextButtonTooltip key should be updated.
 * @param  {Boolean} status
 * @return {Object}
 */
export function setNextButtonTooltipDisplay(status) {
  return {
    type: SHOW_NEXT_BUTTON_ENABLED_TOOLTIP,
    value: status,
  };
}

/**
 * Action that sets the flag telling if the crop area is in rotation mode or not.
 * @param {Boolean} rotationMode The rotate flag.
 * @return {Object}
 * @property {String} type  `SET_ROTATION_MODE` constant
 * @property {Boolean} value The `rotationMode` parameter
 */
export function setRotationMode(rotationMode) {
  return {
    type: SET_ROTATION_MODE,
    value: rotationMode,
  };
}

/**
 * Action that sets the flag telling if the crop endpoint has been called.
 * @param {Boolean} called
 * @return {Object}
 * @property {String} type  `SET_CROP_ENDPOINT_CALLED` constant
 * @property {Boolean} value The `called` parameter
 */
export function setCropEndpointCalled(called) {
  return {
    type: SET_CROP_ENDPOINT_CALLED,
    value: called,
  };
}



// WEBPACK FOOTER //
// ./src/actions/crop.js